import React, { useEffect, useState } from "react";
import axios from "axios";
import api, { API_BASE, formatErrorDetail } from "@/lib/api";
import { Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { deactivateShiftConfirm } from "./messages";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BLANK = { name: "", code: "", start_time: "08:00", end_time: "17:00", grace_minutes: 15,
                workdays: [0, 1, 2, 3, 4], break_minutes: "", required_hours: "", ot_threshold_hours: "",
                // Shift policy, all optional: leaving them blank keeps the shift paid exactly as before.
                flexi: false, early_in_time: "", half_day_hours: "", absent_below_hours: "", tardy_after_minutes: "",
                is_active: true };

// Gross span in minutes, wrapping past midnight — mirrors backend worktime.gross_minutes.
function grossMinutes(start, end) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const s = sh * 60 + sm, e = eh * 60 + em;
    return e > s ? e - s : 24 * 60 - s + e;
}

// GET /shifts/roster for ONE specific company, bypassing the shared `api`
// instance on purpose: its request interceptor unconditionally overwrites
// X-Company-Id with the browser's ACTIVE company on every call, so it has
// no way to ask for a different company's roster. deactivate_shift below
// needs exactly that, for a shift with no company_id of its own (the
// common case -- ShiftDefinitions never exposes a company_id field, so
// every shift created through this UI is shared tenant-wide by
// construction). Mirrors the two headers the interceptor itself sets.
async function rosterForCompany(companyId) {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
    const headers = { "X-Company-Id": companyId };
    if (token) headers.Authorization = `Bearer ${token}`;
    const { data } = await axios.get(`${API_BASE}/shifts/roster`, { headers });
    return data;
}

// The employees a deactivate of `sh` would actually reach, scoped the same
// way the WRITE is scoped: `deactivate_shift` -> `refresh_shift_cache`
// takes no company filter at all, so a shared shift (company_id: null,
// what this UI always creates) reaches every company in the tenant, not
// just whichever one is active in the browser right now -- the same shape
// rotation_service._reached_employee_ids already uses (tenant-wide unless
// the rule itself is company-scoped). A shift that DOES carry a
// company_id is bounded to that one company, because every assignment
// pointing at it was itself validated at write time to belong to the same
// company (shift_service.create_assignment's company-scope check).
//
// Returns undefined -- never a possibly-wrong number -- if any company's
// roster couldn't be read (a network failure, or a company the caller
// lacks a role in and so can never see the true tenant-wide total for).
async function affectedByDeactivate(sh) {
    if (sh.company_id) {
        const data = await rosterForCompany(sh.company_id);
        return (data.items || []).filter((i) => i.shift_id === sh.id).length;
    }
    const { data: companiesData } = await api.get("/hris/companies");
    const companies = Array.isArray(companiesData) ? companiesData : companiesData.items || [];
    const rosters = await Promise.all(companies.map((c) => rosterForCompany(c.id)));
    return rosters.reduce((sum, r) => sum + (r.items || []).filter((i) => i.shift_id === sh.id).length, 0);
}

export default function ShiftDefinitions({ shifts, reload }) {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(BLANK);

    const open = (sh) => {
        setEditing(sh || "new");
        setForm(sh ? { ...BLANK, ...sh, break_minutes: sh.break_minutes ?? "",
                       required_hours: sh.required_hours ?? "", ot_threshold_hours: sh.ot_threshold_hours ?? "",
                       flexi: !!sh.flexi, early_in_time: sh.early_in_time ?? "", half_day_hours: sh.half_day_hours ?? "",
                       absent_below_hours: sh.absent_below_hours ?? "", tardy_after_minutes: sh.tardy_after_minutes ?? "" }
                   : BLANK);
    };

    const save = async () => {
        const body = { ...form,
            grace_minutes: Number(form.grace_minutes) || 0,
            break_minutes: form.break_minutes === "" ? null : Number(form.break_minutes),
            required_hours: form.required_hours === "" ? null : Number(form.required_hours),
            ot_threshold_hours: form.ot_threshold_hours === "" ? null : Number(form.ot_threshold_hours),
            flexi: !!form.flexi,
            early_in_time: form.early_in_time === "" ? null : form.early_in_time,
            half_day_hours: form.half_day_hours === "" ? null : Number(form.half_day_hours),
            absent_below_hours: form.absent_below_hours === "" ? null : Number(form.absent_below_hours),
            tardy_after_minutes: form.tardy_after_minutes === "" ? null : Number(form.tardy_after_minutes),
            // Explicit rather than relying on the spread: `validate_shift_payload`
            // defaults a missing `is_active` to true, so leaving this out of a PUT
            // body would silently reactivate a shift the user never touched.
            is_active: form.is_active !== false };
        try {
            if (editing === "new") await api.post("/shifts", body);
            else await api.put(`/shifts/${editing.id}`, body);
            toast.success(editing === "new" ? "Shift created" : "Shift updated");
            setEditing(null); reload();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const deactivate = async (sh) => {
        // The real blast radius, scoped the way the write behaves (see
        // affectedByDeactivate above) -- not just the active company's
        // roster, which would undercount every shared shift this UI can
        // create. Any failure (including "can't see every company this
        // reaches") leaves affectedCount undefined; deactivateShiftConfirm
        // never shows a number it wasn't given.
        let affectedCount;
        try { affectedCount = await affectedByDeactivate(sh); }
        catch { affectedCount = undefined; }
        if (!window.confirm(deactivateShiftConfirm(sh, affectedCount))) return;
        try { await api.delete(`/shifts/${sh.id}`); toast.success("Deactivated"); reload(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const gross = grossMinutes(form.start_time, form.end_time);
    const paid = (gross - (Number(form.break_minutes) || 0)) / 60;

    return (
        <Surface className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                    Clocking in after start + grace marks the employee late. A shift with no break or
                    required hours behaves exactly as it did before this module — those fields are opt-in.
                </div>
                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                        onClick={() => open(null)} data-testid="new-shift-btn"><Plus className="size-4" /> New Shift</button>
            </div>

            <div className="grid gap-2 md:grid-cols-3">
                {shifts.map((sh) => (
                    <div key={sh.id} className={`rounded-lg border p-3 ${sh.is_active === false ? "opacity-50" : ""}`}
                         data-testid={`shift-${sh.code}`}>
                        <div className="flex items-center justify-between">
                            <b className="text-sm">{sh.name}</b>
                            <span className="badge badge-info">{sh.assignment_count} rule(s)</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 font-mono">
                            {sh.start_time}–{sh.end_time}{sh.crosses_midnight ? " next day" : ""} · grace {sh.grace_minutes}m
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {(sh.workdays || []).map((d) => DAYS[d]).join(" ")}
                            {sh.break_minutes ? ` · ${sh.break_minutes}m unpaid break` : ""}
                            {sh.required_hours ? ` · ${sh.required_hours}h required` : ""}
                        </div>
                        {(sh.flexi || sh.early_in_time || sh.half_day_hours || sh.absent_below_hours || sh.tardy_after_minutes) && (
                            <div className="text-xs text-muted-foreground" data-testid={`shift-policy-${sh.code}`}>
                                {sh.flexi ? "Flexi" : "Fixed"}
                                {sh.early_in_time ? ` · counts from ${sh.early_in_time}` : ""}
                                {sh.half_day_hours ? ` · half day ${sh.half_day_hours}h` : ""}
                                {sh.absent_below_hours ? ` · absent under ${sh.absent_below_hours}h` : ""}
                                {sh.tardy_after_minutes ? ` · half day past ${sh.tardy_after_minutes}m` : ""}
                            </div>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs">
                            <button className="text-primary" onClick={() => open(sh)} data-testid={`edit-shift-${sh.code}`}>Edit</button>
                            {sh.is_active !== false &&
                                <button className="text-destructive ml-auto" onClick={() => deactivate(sh)}>Deactivate</button>}
                        </div>
                    </div>
                ))}
                {!shifts.length && <Empty title="No shifts yet" subtitle="Create one, then assign it on the Assignments tab." />}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing === "new" ? "Create shift" : `Edit ${form.name}`}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Name</Label><Input value={form.name} data-testid="shift-name"
                                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Night Shift" /></div>
                            <div><Label>Code</Label><Input value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="NIGHT" /></div>
                        </div>
                        {editing !== "new" && (
                            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                                <input type="checkbox" id="shift-active" data-testid="shift-active"
                                       checked={form.is_active !== false}
                                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                <Label htmlFor="shift-active" className="!mb-0 cursor-pointer">
                                    Active
                                    <span className="block text-xs text-muted-foreground font-normal">
                                        Untick to deactivate, tick to reactivate — attendance history keeps
                                        referencing this shift either way.
                                    </span>
                                </Label>
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                            <div><Label>Start</Label><Input type="time" value={form.start_time}
                                onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                            <div><Label>End</Label><Input type="time" value={form.end_time}
                                onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                            <div><Label>Grace (min)</Label><Input type="number" value={form.grace_minutes}
                                onChange={(e) => setForm({ ...form, grace_minutes: e.target.value })} /></div>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono" data-testid="shift-span">
                            {form.start_time} → {form.end_time}{grossMinutes(form.start_time, form.end_time) !== 0 &&
                                form.end_time < form.start_time ? " next day" : ""} · {(gross / 60).toFixed(2)}h gross
                            {form.break_minutes !== "" ? ` / ${paid.toFixed(2)}h paid` : ""}
                        </div>
                        <div>
                            <Label>Workdays — the days the shift STARTS</Label>
                            <div className="flex gap-2 mt-1 flex-wrap">
                                {DAYS.map((d, i) => (
                                    <label key={d} className="flex items-center gap-1 text-sm">
                                        <input type="checkbox" data-testid={`workday-${i}`}
                                               checked={(form.workdays || []).includes(i)}
                                               onChange={(ev) => setForm({ ...form, workdays:
                                                   ev.target.checked ? [...form.workdays, i].sort()
                                                                     : form.workdays.filter((x) => x !== i) })} /> {d}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div><Label>Unpaid break (min)</Label><Input type="number" value={form.break_minutes}
                                placeholder="none" onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} /></div>
                            <div><Label>Required hours</Label><Input type="number" step="0.25" value={form.required_hours}
                                placeholder="none" onChange={(e) => setForm({ ...form, required_hours: e.target.value })} /></div>
                            <div><Label>OT threshold</Label><Input type="number" step="0.25" value={form.ot_threshold_hours}
                                placeholder="= required" onChange={(e) => setForm({ ...form, ot_threshold_hours: e.target.value })} /></div>
                        </div>
                        <div className="rounded-md border p-3 space-y-2" data-testid="shift-policy-section">
                            <div className="text-sm font-medium">Attendance policy (optional)</div>
                            <div className="text-xs text-muted-foreground">
                                Leave all of these empty and the shift is paid exactly as before. Fill any one in and the
                                day is read in this order: no clock-in or clock-out is absent; fewer hours than the minimum
                                is absent; late or undertime past the limit is a half day with no minutes charged;
                                anything else is a full day, with late and undertime charged as minutes. Absences are then
                                counted against this shift&apos;s own workdays above, not Monday to Friday.
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="shift-flexi" data-testid="shift-flexi" checked={!!form.flexi}
                                       onChange={(e) => setForm({ ...form, flexi: e.target.checked })} />
                                <Label htmlFor="shift-flexi" className="!mb-0 cursor-pointer">
                                    Flexi (no fixed start)
                                    <span className="block text-xs text-muted-foreground font-normal">
                                        Never late; hours are counted from the clock-in.
                                    </span>
                                </Label>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label>Earliest time counted</Label><Input type="time" value={form.early_in_time} data-testid="shift-early-in"
                                    onChange={(e) => setForm({ ...form, early_in_time: e.target.value })} />
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Clock-ins before this count from this time.</div></div>
                                <div><Label>Half day (hours)</Label><Input type="number" step="0.25" value={form.half_day_hours} placeholder="none" data-testid="shift-half-day-hours"
                                    onChange={(e) => setForm({ ...form, half_day_hours: e.target.value })} />
                                    <div className="text-[10px] text-muted-foreground mt-0.5">Replaces the 4 hour default of the company half-day rule.</div></div>
                                <div><Label>Absent below (hours)</Label><Input type="number" step="0.25" value={form.absent_below_hours} placeholder="none" data-testid="shift-absent-below"
                                    onChange={(e) => setForm({ ...form, absent_below_hours: e.target.value })} /></div>
                                <div><Label>Half day when late or short by more than (min)</Label><Input type="number" value={form.tardy_after_minutes} placeholder="none" data-testid="shift-tardy-after"
                                    onChange={(e) => setForm({ ...form, tardy_after_minutes: e.target.value })} /></div>
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Leaving break and required hours blank keeps this shift's pay arithmetic exactly
                            as it is today. Night differential is always measured from actual timestamps
                            (Art. 86) and is not driven by these fields.
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm"
                                onClick={save} data-testid="shift-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Surface>
    );
}
